import { useEffect, useState, useRef } from "react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import Brand from "../components/Brand";
import Plate from "../components/Plate";
import SbrFooter from "../components/SbrFooter";
import SlCloudFooter from "../brands/servicelad/SlCloudFooter";
import SlCloudShell from "../brands/servicelad/SlCloudShell";
import SlReport from "../brands/servicelad/SlReport";
import slReportCss from "../brands/servicelad/sl-report.css?raw";
import usePageMeta from "../hooks/usePageMeta";
import reportCss from "../styles/report.css?raw";
import { formatUkRegistration } from "../utils/registration";
import { findMakeByDbMake, findMakeBySlug } from "../../lib/vehicle/makes.js";
import { choosePageEnd, mergeSpans, safeCanvasScale } from "../utils/pdfPageBreaks.js";

function formatDate(dateStr) {
  if (!dateStr) return "Not available";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return dateStr;
  }
}

function formatCheckedOn(dateStr) {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString("en-GB");
  } catch {
    return null;
  }
}

function formatMileage(value, unit) {
  if (value == null) return "Not recorded";
  const miles = unit === "km" ? Math.round(value * 0.621371) : value;
  return `${new Intl.NumberFormat("en-GB").format(miles)} mi`;
}

// Provider data sometimes puts L/100km figures in MPG fields (a van showing
// "2.8 mpg"). No road car falls outside this range, so hide those values.
function plausibleMpg(value) {
  const mpg = Number(value);
  return mpg >= 10 && mpg <= 250 ? value : null;
}

function valuationAmount(value) {
  const amount = typeof value === "number"
    ? value
    : Number(String(value ?? "").replace(/[£,\s]/g, ""));
  return Number.isFinite(amount) && amount > 0 ? amount : null;
}

function formatValuation(value) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 0,
  }).format(value);
}

function ServiceEventCard({ event, index }) {
  return (
    <div className="event-card" key={index}>
      <div className="event-marker">
        <span className="event-dot">{index + 1}</span>
        {index > 0 && <span className="event-line" />}
      </div>
      <div className="event-body">
        <div className="event-header">
          <strong>{formatDate(event.date_of_service_event)}</strong>
          <span className="event-mileage">{formatMileage(event.mileage_observed, event.mileage_unit)}</span>
        </div>
        <div className="event-provider">
          {event.service_provider || "Not recorded"}
        </div>
        {event.service_actions && event.service_actions.length > 0 ? (
          <ul className="event-actions">
            {event.service_actions.map((action, i) => (
              <li key={i} className={action === "unknown." ? "action-unknown" : ""}>
                {action === "unknown." ? "Details not recorded" : action}
              </li>
            ))}
          </ul>
        ) : (
          <p className="event-no-actions">No service actions recorded</p>
        )}
      </div>
    </div>
  );
}

// Summary strip values. A check the provider did not return is "Not checked",
// never shown as clear.
function buildQuickChecks({ rc, vehicle }) {
  const known = rc?.available;
  const writeoffs = rc?.writeoffs || [];
  const finance = rc?.finance || [];
  const notChecked = { value: "Not checked", tone: "muted" };
  const checks = [
    { label: "Stolen", href: "#security", ...(!known ? notChecked : rc.stolen ? { value: "Marker found", tone: "bad" } : { value: "Clear", tone: "ok" }) },
    { label: "Write-off", href: "#security", ...(!known ? notChecked : writeoffs.length ? { value: `${writeoffs.length} found`, tone: "bad" } : { value: "Clear", tone: "ok" }) },
    { label: "Finance", href: "#security", ...(!known ? notChecked : rc.hasFinance ? { value: `${finance.length || 1} found`, tone: "bad" } : { value: "Clear", tone: "ok" }) },
  ];
  if (known && rc.scrapped) {
    checks.push({ label: "Imported", href: "#security", ...(rc.scrapped.imported ? { value: "Yes", tone: "warn" } : { value: "No", tone: "ok" }) });
  }
  if (vehicle?.mot_status) {
    checks.push({ label: "MOT", href: "#mot", value: vehicle.mot_status, tone: vehicle.mot_status === "Valid" ? "ok" : "bad" });
  }
  if (vehicle?.tax_status) {
    checks.push({ label: "Tax", href: "#mot", value: vehicle.tax_status, tone: vehicle.tax_status === "Taxed" ? "ok" : "bad" });
  }
  return checks;
}

// html2canvas draws our fonts about 0.35em lower than the browser lays them
// out, so text slid out of its pills and the title overlapped the subtitle.
// Only the throwaway clone is changed; the page itself is untouched.
function liftTextForPdf(root) {
  const doc = root.ownerDocument;
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    // SVG text (chart labels) must stay inside its <text>; a wrapper tag would break the SVG.
    acceptNode: (node) => (node.nodeValue.trim() && !node.parentElement?.closest("svg") ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT),
  });
  const textNodes = [];
  while (walker.nextNode()) textNodes.push(walker.currentNode);
  for (const node of textNodes) {
    // A custom tag, not <span>: rules like `.quick-check span` must not match it.
    const lift = doc.createElement("pdf-lift");
    lift.style.position = "relative";
    lift.style.top = "-0.35em";
    node.parentNode.insertBefore(lift, node);
    lift.appendChild(node);
  }
}

function StatusBadge({ status, label }) {
  const classes = { clear: "badge-clear", warning: "badge-warning", danger: "badge-danger", pending: "badge-pending" };
  return <span className={`report-badge ${classes[status] || classes.pending}`}>{label}</span>;
}

function RiskOkIcon() {
  return (
    <img
      src="/svgs/ok.png"
      alt=""
      width={31}
      height={31}
      className="risk-ok-icon"
      aria-hidden="true"
    />
  );
}

function RiskWarningIcon() {
  return (
    <img
      src="/svgs/warning.svg"
      alt=""
      width={31}
      height={31}
      className="risk-ok-icon"
      aria-hidden="true"
    />
  );
}

export default function ReportPage({ brand }) {
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token") || "";
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const reportRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  usePageMeta({
    title: `Vehicle Report | ${brand.name}`,
    description: "Your vehicle history report.",
    robots: "noindex, nofollow",
  });

  useEffect(() => {
    if (!token) {
      setError("No report token provided.");
      setLoading(false);
      return;
    }
    fetch(`/api/reports/${token}`)
      .then(async (res) => {
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || "Failed to load report");
        }
        return res.json();
      })
      .then((d) => setData(d))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  const handleDownloadPdf = async () => {
    if (!reportRef.current) return;
    setDownloading(true);
    // Open every "See more" list so the PDF has the full report; cut points
    // below are measured after this, so they match the snapshot.
    reportRef.current.classList.add("slr-expand-all");
    // ServiceLad: always the desktop layout at a fixed width, so a phone
    // makes the same A4 pages as a computer instead of 20+ narrow ones.
    const slReport = reportRef.current.querySelector(".slr");
    if (slReport) {
      slReport.classList.add("slr-pdf");
      reportRef.current.style.width = "920px";
      reportRef.current.style.maxWidth = "none";
    }
    try {
      const container = reportRef.current;
      const canvas = await html2canvas(container, {
        scale: safeCanvasScale(container.offsetWidth, container.offsetHeight),
        backgroundColor: "#ffffff",
        useCORS: true,
        onclone: (_doc, clonedReport) => liftTextForPdf(clonedReport),
      });
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 10;
      const contentWidth = pageWidth - margin * 2;
      const contentHeight = pageHeight - margin * 2;
      // How many source-canvas pixels fit on one PDF page at contentWidth.
      const sliceHeightPx = (canvas.width / contentWidth) * contentHeight;

      // Use the horizontal scale: html2canvas sets canvas width to exactly
      // element width x scale, while the height can drift by a few px, which
      // would push cut points off-target further down the page.
      const pxPerCssPx = canvas.width / container.offsetWidth;
      const containerTop = container.getBoundingClientRect().top;
      const toCanvas = (y) => (y - containerTop) * pxPerCssPx;

      // Card and block edges: preferred page breaks, so cards stay whole.
      // 2px inside each edge, so a break never carries a sliver of the next card's border.
      const edges = [];
      container
        .querySelectorAll(
          ".report-title-bar, .report-vehicle-image, .report-meta, .dashboard-section, .dashboard-columns, .report-section, .metric-card, .status-panel, .mot-donut-panel, .event-card, .risk-card, .risk-detail-row, .risk-details, .report-empty, .report-footer, [data-pdf-block]"
        )
        .forEach((el) => {
          const rect = el.getBoundingClientRect();
          edges.push(toCanvas(rect.top - 2), toCanvas(rect.bottom + 2));
        });
      edges.sort((a, b) => a - b);

      // Every line of text, image and chart: a page never ends inside one.
      // 3px either side covers html2canvas drawing text slightly off its box.
      const spans = [];
      const range = document.createRange();
      const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
      while (walker.nextNode()) {
        const node = walker.currentNode;
        if (!node.nodeValue.trim() || node.parentElement?.closest("svg")) continue;
        range.selectNodeContents(node);
        for (const rect of range.getClientRects()) spans.push([toCanvas(rect.top - 3), toCanvas(rect.bottom + 3)]);
      }
      container.querySelectorAll("img, svg").forEach((el) => {
        if (el.parentElement?.closest("svg")) return;
        const rect = el.getBoundingClientRect();
        spans.push([toCanvas(rect.top - 3), toCanvas(rect.bottom + 3)]);
      });
      const safeSpans = mergeSpans(spans);

      // Blocks that must end their page (the ServiceLad summary is page one on its own).
      const forcedBreaks = [...container.querySelectorAll("[data-pdf-break-after]")]
        .map((el) => toCanvas(el.getBoundingClientRect().bottom))
        .sort((a, b) => a - b);

      // Render page-by-page slices instead of placing one giant image.
      // Keeps margins on every page, avoids oversized canvases, and JPEG
      // keeps the file small (PNG of the full report was 50MB+).
      let renderedPx = 0;
      let firstPage = true;
      while (renderedPx < canvas.height - 1) {
        const sliceEnd = choosePageEnd({
          start: renderedPx,
          pageHeight: sliceHeightPx,
          total: canvas.height,
          edges,
          spans: safeSpans,
          forced: forcedBreaks,
        });
        const slice = document.createElement("canvas");
        slice.width = canvas.width;
        slice.height = Math.max(1, Math.round(sliceEnd - renderedPx));
        const sliceCtx = slice.getContext("2d");
        sliceCtx.fillStyle = "#ffffff";
        sliceCtx.fillRect(0, 0, slice.width, slice.height);
        sliceCtx.drawImage(canvas, 0, renderedPx, canvas.width, slice.height, 0, 0, canvas.width, slice.height);
        const imgData = slice.toDataURL("image/jpeg", 0.8);
        if (!firstPage) pdf.addPage();
        const sliceHeightMm = (slice.height * contentWidth) / canvas.width;
        pdf.addImage(imgData, "JPEG", margin, margin, contentWidth, sliceHeightMm);
        renderedPx = sliceEnd;
        firstPage = false;
      }

      const reg = data?.vehicle?.registration || "vehicle";
      pdf.save(`${reg.replace(/[^A-Z0-9]/gi, "")}_report.pdf`);
    } catch {
      setError("Failed to generate PDF. Please try again.");
    } finally {
      reportRef.current?.classList.remove("slr-expand-all");
      if (slReport) {
        slReport.classList.remove("slr-pdf");
        reportRef.current?.style.removeProperty("width");
        reportRef.current?.style.removeProperty("max-width");
      }
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <>
        <style>{reportCss}</style>
        <div className="report-loading">
          <div className="report-spinner" />
          <p>Generating your report...</p>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <style>{reportCss}</style>
        <div className="report-error-page">
          <h1>Report unavailable</h1>
          <p>{error}</p>
          <a href="/" className="report-back">Back to homepage</a>
        </div>
      </>
    );
  }

  const { report, order } = data;
  const vehicle = report?.vehicle || data.vehicle;
  const reg = vehicle?.registration || "";
  const displayReg = formatUkRegistration(reg);
  const motTests = report.mot_history || [];
  const motSummary = report.mot_summary || {};
  const passedMots = motSummary.pass_count ?? motTests.filter((test) => String(test.result || "").toUpperCase() === "PASSED").length;
  const failedMots = motSummary.fail_count ?? motTests.filter((test) => String(test.result || "").toUpperCase() === "FAILED").length;
  const countedMots = passedMots + failedMots;
  const motPassRate = motSummary.pass_rate ?? (countedMots ? Math.round((passedMots / countedMots) * 100) : null);
  const latestMileage = motSummary.latest_mileage ?? motTests.find((test) => test.mileage != null)?.mileage ?? null;
  const firstUsedYear = vehicle?.first_used_date ? new Date(vehicle.first_used_date).getFullYear() : vehicle?.model_year;
  const vehicleAge = firstUsedYear ? Math.max(0, new Date().getFullYear() - firstUsedYear) : null;

  // Extract real specs from CheckCarDetails data. Two response shapes exist:
  // the MOTCO-era cached shape (VehicleRegistration / Engine / Consumption /
  // vedRate) and the current live-API shape (VehicleIdentification /
  // PowerSource / VehicleExciseDutyDetails / ...). Read both, so reports
  // render fully no matter which shape was stored at purchase time.
  const specs = report.vehicle_specs;
  const vr = specs?.VehicleRegistration || {};
  const vi = specs?.VehicleIdentification || {};
  const dims = specs?.Dimensions || {};
  const weights = specs?.Weights || {};
  const bodyDetails = specs?.BodyDetails || {};
  const tech = specs?.DvlaTechnicalDetails || {};
  const modelData = specs?.ModelData || {};
  const ice = specs?.PowerSource?.IceDetails || {};
  const emissions = specs?.Emissions || {};
  const exciseVed = specs?.VehicleExciseDutyDetails?.VedRate || {};
  const trans = specs?.Transmission || {};
  const eng = specs?.Engine || {};
  const perf = specs?.Performance || {};
  const perfStats = perf?.Statistics || {};
  const fuelEco = perf?.FuelEconomy || {};
  const cons = specs?.Consumption || {};
  const smmt = specs?.SmmtDetails || {};
  const gen = specs?.General || {};
  const ved = specs?.vedRate || {};

  const specsEngineCc = smmt.EngineCapacity || vr.EngineCapacity || tech.EngineCapacityCc || ice.EngineCapacityCc || smmt.NominalEngineCapacity || vehicle?.engine_capacity || null;
  const specsEngineDesc = eng.Description || ice.EngineDescription || null;
  const specsEngine = specsEngineDesc || (specsEngineCc ? `${specsEngineCc}cc` : null);
  const specsPower = perf?.Power?.Bhp ? `${perf.Power.Bhp} BHP` : null;
  const specsTorque = perf?.Torque?.Nm ? `${perf.Torque.Nm} Nm` : null;
  const specsTransmission = smmt.Transmission || vr.Transmission || trans.TransmissionType || null;
  const zeroToSixty = perf?.Acceleration?.ZeroTo60Mph || perfStats.ZeroToSixtyMph || null;
  const specsZeroToSixty = zeroToSixty ? `${zeroToSixty}s` : null;
  const topSpeed = perf?.MaxSpeed?.Mph || perfStats.MaxSpeedMph || null;
  const specsTopSpeed = topSpeed ? `${topSpeed} mph` : null;
  const combinedMpg = plausibleMpg(cons?.Combined?.Mpg || fuelEco.CombinedMpg);
  const specsMpg = combinedMpg ? `${combinedMpg} mpg` : null;
  const specsDoors = vr.DoorPlanLiteral || dims.NumberOfDoors || bodyDetails.NumberOfDoors || smmt.NumberOfDoors || null;
  const specsSeats = vr.SeatingCapacity || dims.NumberOfSeats || bodyDetails.NumberOfSeats || tech.SeatCountIncludingDriver || smmt.NumberOfSeats || null;
  const specsBodyStyle = smmt.BodyStyle || bodyDetails.BodyStyle || vi.DvlaBodyType || null;
  const kerbWeight = dims.KerbWeight || weights.KerbWeightKg || smmt.KerbWeight || null;
  const specsKerbWeight = kerbWeight ? `${kerbWeight} kg` : null;
  const grossWeight = dims.GrossVehicleWeight || weights.GrossVehicleWeightKg || tech.GrossWeight || null;
  const specsGrossWeight = grossWeight ? `${grossWeight} kg` : null;
  const specsCo2 = perf?.Co2 || vr.Co2Emissions || emissions.ManufacturerCo2 || vehicle?.co2_emissions || null;
  const specsNcap = perf?.NoiseLevel ? `${perf.NoiseLevel} dB` : null;
  const lengthMm = dims.CarLength || dims.LengthMm || smmt.Length || null;
  const specsLength = lengthMm ? `${lengthMm} mm` : null;
  const widthMm = dims.Width || dims.WidthMm || smmt.Width || null;
  const specsWidth = widthMm ? `${widthMm} mm` : null;
  const heightMm = dims.Height || dims.HeightMm || smmt.Height || null;
  const specsHeight = heightMm ? `${heightMm} mm` : null;
  const wheelbaseMm = dims.WheelBase || dims.WheelBaseLengthMm || null;
  const specsWheelbase = wheelbaseMm ? `${wheelbaseMm} mm` : null;
  const fuelTank = dims.FuelTankCapacity || bodyDetails.FuelTankCapacityLitres || null;
  const specsFuelTank = fuelTank ? `${fuelTank} litres` : null;
  const specsAxles = dims.NumberOfAxles || bodyDetails.NumberOfAxles || smmt.NumberOfAxles || null;
  const specsGears = smmt.NumberOfGears || vr.GearCount || trans.NumberOfGears || null;
  const specsDriveType = smmt.DriveType || trans.DriveType || null;
  const specsEuroStatus = gen.EuroStatus || modelData.EuroStatus || smmt.EuroStatus || null;
  const specsAspiration = eng.Aspiration || ice.Aspiration || null;
  const specsCylinders = eng.NumberOfCylinders || ice.NumberOfCylinders || null;
  const specsValveGear = eng.ValveGear || ice.ValveGear || null;
  const specsEngineLocation = eng.Location || ice.EngineLocation || null;
  const specsEngineMake = eng.Make || ice.EngineManufacturer || null;
  const specsFuelCatalyst = eng.FuelCatalyst || (emissions.HasFuelCatalyst === true ? "Yes" : emissions.HasFuelCatalyst === false ? "No" : null);
  const specsVin = vr.Vin || vi.Vin || null;
  const specsVinLast5 = vr.VinLast5 || vi.VinLast5 || null;
  const specsYear = vr.YearOfManufacture || vi.YearOfManufacture || vehicle?.model_year || null;
  const specsImported = vr.Imported || null;
  const specsExported = vr.Exported || null;
  const specsScrapped = vr.Scrapped || null;
  const taxAnnual = ved?.Standard?.TwelveMonth || exciseVed?.Standard?.TwelveMonths || null;
  const specsTaxAnnual = taxAnnual ? `£${taxAnnual}` : null;
  const taxSixMonth = ved?.Standard?.SixMonth || exciseVed?.Standard?.SixMonths || null;
  const specsTaxSixMonth = taxSixMonth ? `£${taxSixMonth}` : null;
  const urbanMpg = plausibleMpg(cons?.UrbanCold?.Mpg || fuelEco.UrbanColdMpg);
  const specsUrbanMpg = urbanMpg ? `${urbanMpg} mpg` : null;
  const extraUrbanMpg = plausibleMpg(cons?.ExtraUrban?.Mpg || fuelEco.ExtraUrbanMpg);
  const specsExtraUrbanMpg = extraUrbanMpg ? `${extraUrbanMpg} mpg` : null;
  const specsEngineNumber = vr.EngineNumber || vi.EngineNumber || null;
  const specsFuelSystem = eng.FuelSystem || ice.FuelDelivery || null;

  // Hybrid fallbacks: CheckCarDetails specs first, then DVLA/DVSA vehicle data.
  // Overview "Engine size" shows the cc figure only; the engine's own
  // description code (e.g. "D4FD EURO6") lives in the Specs section instead,
  // so it never competes with the separate Euro-standard row.
  const engineSizeDisplay = specsEngineCc ? `${specsEngineCc}cc` : (vehicle?.engine_capacity ? `${vehicle.engine_capacity}cc` : null);
  const transmissionDisplay = specsTransmission || vehicle?.transmission || null;
  // Full VIN: paying customers see the complete number (from the history
  // data), falling back to the service-history VIN. Shown once, in Overview.
  const fullVin = report.risk_checks?.scrapped?.vin || report.service_history?.vin || null;

  // Detail panels hide rows with no data instead of printing "Not available".
  const detailRow = ([label, value]) => (
    <div className="detail-row" key={label}><span>{label}</span><strong>{value}</strong></div>
  );
  const hasValue = ([, value]) => value != null && value !== "";

  const specRows = [
    ["Make", vehicle?.make], ["Model", vehicle?.model], ["Year", specsYear],
    ["Engine", specsEngine], ["Fuel", vehicle?.fuel_type],
    ["Mileage", latestMileage ? formatMileage(latestMileage, "mi") : null],
    // Colour is not repeated here, it's already shown as an Overview tile.
    ["First used", vehicle?.first_used_date ? formatDate(vehicle.first_used_date) : null],
    ["Body style", specsBodyStyle], ["Doors", specsDoors], ["Seats", specsSeats],
    ["Gears", specsGears], ["Drive type", specsDriveType], ["Euro standard", specsEuroStatus],
  ].filter(hasValue);

  const perfRows = [
    ["Power", specsPower], ["Torque", specsTorque],
    ["0-60 mph", specsZeroToSixty], ["Top speed", specsTopSpeed],
    ["Combined MPG", specsMpg], ["Urban MPG", specsUrbanMpg], ["Extra urban MPG", specsExtraUrbanMpg],
    ["CO2", specsCo2 ? `${specsCo2} g/km` : null],
    ["Road tax (annual)", specsTaxAnnual], ["Road tax (6 months)", specsTaxSixMonth],
    ["MOT status", vehicle?.mot_status || null],
    ["MOT expiry", vehicle?.mot_expiry_date ? formatDate(vehicle.mot_expiry_date) : null],
  ].filter(hasValue);

  const dimRows = [
    ["Length", specsLength], ["Width", specsWidth], ["Height", specsHeight],
    ["Wheelbase", specsWheelbase], ["Kerb weight", specsKerbWeight],
    ["Gross weight", specsGrossWeight], ["Fuel tank", specsFuelTank], ["Axles", specsAxles],
  ].filter(hasValue);

  const engRows = [
    ["VIN", fullVin],
    ["Engine number", specsEngineNumber], ["Aspiration", specsAspiration],
    ["Cylinders", specsCylinders], ["Valve gear", specsValveGear],
    ["Engine location", specsEngineLocation], ["Engine make", specsEngineMake],
    ["Fuel catalyst", specsFuelCatalyst], ["Fuel system", specsFuelSystem],
  ].filter(hasValue);

  const overviewMetrics = [
    { icon: "/svgs/year.svg", value: vehicle?.model_year, label: "Age" },
    { icon: "/svgs/engine.svg", value: engineSizeDisplay, label: "Engine size" },
    { icon: "/svgs/fuel.svg", value: vehicle?.fuel_type, label: "Fuel type" },
    { icon: "/svgs/milage.svg", value: latestMileage ? formatMileage(latestMileage, "mi") : null, label: "Mileage" },
    { icon: "/svgs/colour.svg", value: vehicle?.colour, label: "Colour" },
    { icon: "/svgs/transmission.svg", value: transmissionDisplay, label: "Transmission" },
    { fallbackIcon: "◆", value: report.insurance_group ? `Group ${report.insurance_group}` : null, label: "Insurance group" },
  ].filter(({ value }) => value != null && value !== "");

  // Risk checks
  const rc = report.risk_checks;
  const isSbr = brand.key === "servicebyreg";
  const valuation = report.valuation?.ValuationList || {};
  const valuationRows = [
    ["Dealer forecourt", valuation.DealerForecourt],
    ["Trade retail", valuation.TradeRetail],
    ["Private sale", valuation.PrivateClean],
    ["Private typical", valuation.PrivateAverage],
    ["Part exchange", valuation.PartExchange],
    ["Trade average", valuation.TradeAverage],
    ["Auction", valuation.Auction],
  ].map(([label, value]) => [label, valuationAmount(value)])
    .filter(([, value]) => value !== null);
  const valuationMileage = valuationAmount(report.valuation?.Mileage);
  const recall = vehicle?.has_outstanding_recall;
  const recallKnown = recall === true || recall === false;
  const checkedOn = formatCheckedOn(report.generated_at);
  const makeDef = vehicle?.make ? findMakeByDbMake(vehicle.make) || findMakeBySlug(vehicle.make) : null;
  const makeLogo = makeDef?.file ? `/brands/${makeDef.file}` : null;

  // ServiceByReg leads with the buyer's big questions (Caroat style): a
  // summary strip, then stolen / write-off / finance before any specs.
  const quickChecks = isSbr ? buildQuickChecks({ rc, vehicle }) : [];
  const securitySection = rc ? (
    <section className="report-section" id="security">
      <h2>{isSbr ? "Stolen, write-off and finance" : "Security Checks"}</h2>
      {!rc.available ? (
        <div className="report-empty">
          <p>Security check data unavailable for this vehicle.</p>
          {rc.note && <small>{rc.note}</small>}
        </div>
      ) : (
        <>
          <div className="risk-grid">
            <div className={`risk-card risk-${rc.stolen ? "danger" : "clear"}`}>
              <div className="risk-card-head">
                {rc.stolen ? <RiskWarningIcon /> : <RiskOkIcon />}
                <StatusBadge status={rc.stolen ? "danger" : "clear"} label={isSbr ? (rc.stolen ? "Marker found" : "Clear") : (rc.stolen ? "Stolen" : "Not stolen")} />
              </div>
              <p>{rc.stolen ? "This vehicle is recorded as stolen. Do not purchase without police clearance." : isSbr ? "No stolen marker found." : "Not recorded as stolen on the national database."}</p>
              {isSbr && <small className="risk-source">Source: Police National Computer</small>}
              {checkedOn && <small className="risk-checked-on">Checked on {checkedOn}</small>}
            </div>
            <div className={`risk-card risk-${rc.writeoffs.length > 0 ? (rc.writeoffs.some(w => /CAT S/i.test(w.status)) ? "danger" : "warning") : "clear"}`}>
              <div className="risk-card-head">
                {rc.writeoffs.length > 0 ? <RiskWarningIcon /> : <RiskOkIcon />}
                <StatusBadge status={rc.writeoffs.length > 0 ? (rc.writeoffs.some(w => /CAT S/i.test(w.status)) ? "danger" : "warning") : "clear"} label={rc.writeoffs.length > 0 ? `${rc.writeoffs.length} Write-off${rc.writeoffs.length > 1 ? "s" : ""}` : isSbr ? "Clear" : "No write-offs"} />
              </div>
              <p>{rc.writeoffs.length > 0 ? "This vehicle has insurance write-off records. See details below." : isSbr ? "No write-off record found." : "No insurance write-offs recorded."}</p>
              {isSbr && <small className="risk-source">Source: MIAFTR, the insurance loss register</small>}
              {checkedOn && <small className="risk-checked-on">Checked on {checkedOn}</small>}
            </div>
            <div className={`risk-card risk-${rc.hasFinance ? "warning" : "clear"}`}>
              <div className="risk-card-head">
                {rc.hasFinance ? <RiskWarningIcon /> : <RiskOkIcon />}
                <StatusBadge status={rc.hasFinance ? "warning" : "clear"} label={isSbr ? (rc.hasFinance ? "Finance found" : "Clear") : (rc.hasFinance ? "Finance" : "No finance")} />
              </div>
              <p>{rc.hasFinance ? `${rc.finance.length} outstanding finance agreement${rc.finance.length > 1 ? "s" : ""}. The lender could legally repossess the vehicle.` : isSbr ? "No finance agreement found." : "No outstanding finance recorded."}</p>
              {isSbr && <small className="risk-source">Source: the UK finance register</small>}
              {checkedOn && <small className="risk-checked-on">Checked on {checkedOn}</small>}
            </div>
            <div className={`risk-card risk-${recall === true ? "danger" : recallKnown ? "clear" : "pending"}`}>
              <StatusBadge
                status={recall === true ? "danger" : recallKnown ? "clear" : "pending"}
                label={recall === true ? "Recall" : recallKnown ? "No recall" : "Not available"}
              />
              <p>{recall === true
                ? "An outstanding safety recall is recorded. This vehicle must be taken to an authorised dealer before driving."
                : recallKnown
                  ? "No outstanding safety recalls recorded."
                  : "Recall information is not available for this vehicle."}</p>
            </div>
          </div>

          {rc.writeoffs.length > 0 && (
            <div className="risk-details">
              <h3>Write-off Details</h3>
              {rc.writeoffs.map((wo, i) => {
                const isCatS = /CAT S/i.test(wo.status || "");
                const insurer = wo.insurerName || wo.insurername || null;
                return (
                  <div className={`risk-detail-row risk-${isCatS ? "danger" : "warning"}`} key={i}>
                    <div><span>Category</span><strong>{wo.status || "Unknown"}</strong></div>
                    <div><span>Date</span><strong>{formatDate(wo.lossdate || wo.lossDate)}</strong></div>
                    {insurer ? <div><span>Insurer</span><strong>{insurer}</strong></div> : null}
                    {wo.showUkBadge ? (
                      <div>
                        <span>Damage location</span>
                        <strong>
                          <img src="/gb.png" alt="" style={{ height: "11px", width: "auto", verticalAlign: "middle", marginRight: "5px", display: "inline-block" }} />
                          United Kingdom
                        </strong>
                      </div>
                    ) : wo.country ? (
                      <div><span>Damage location</span><strong>{wo.country}</strong></div>
                    ) : null}
                    {(wo.damagelocations || wo.damageLocations)?.length > 0 && (
                      <div><span>Damage</span><strong>{(wo.damagelocations || wo.damageLocations).join(", ")}</strong></div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {rc.finance.length > 0 && (
            <div className="risk-details">
              <h3>Finance Details</h3>
              {rc.finance.map((f, i) => (
                <div className="risk-detail-row" key={i}>
                  <div><span>Type</span><strong>{f.agreementtype || "Unknown"}</strong></div>
                  <div><span>Company</span><strong>{f.financecompany || "Not recorded"}</strong></div>
                  {f.dateofagreement && <div><span>Date</span><strong>{formatDate(f.dateofagreement)}</strong></div>}
                </div>
              ))}
            </div>
          )}

          {rc.keepers && (
            <div className="risk-details">
              <h3>Keeper History</h3>
              <div className="risk-detail-row">
                <div><span>Total keepers</span><strong>{rc.keepers.total}</strong></div>
                <div><span>Previous keepers</span><strong>{rc.keepers.previous}</strong></div>
              </div>
              {rc.keepers.changes && rc.keepers.changes.length > 0 && (
                <div className="timeline" style={{ marginTop: "12px" }}>
                  {rc.keepers.changes.map((change, i) => (
                    <div className="event-card" key={i}>
                      <div className="event-marker">
                        <span className="event-dot">{i + 1}</span>
                        {i < rc.keepers.changes.length - 1 && <span className="event-line" />}
                      </div>
                      <div className="event-body">
                        <div className="event-header">
                          <strong>{formatDate(change.DateOfTransaction)}</strong>
                        </div>
                        <div className="event-provider">Keeper change (previous: {change.NumberOfPreviousKeepers})</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {rc.v5cCertificates && rc.v5cCertificates.length > 0 && (
            <div className="risk-details">
              <h3>V5C Certificate History</h3>
              {rc.v5cCertificates.map((cert, i) => (
                <div className="risk-detail-row" key={i}>
                  <div><span>V5C issued</span><strong>{formatDate(cert.CertificateDate)}</strong></div>
                </div>
              ))}
            </div>
          )}

          {rc.plateChanges && rc.plateChanges.length > 0 && (
            <div className="risk-details">
              <h3>Plate Changes</h3>
              {rc.plateChanges.map((change, i) => (
                <div className="risk-detail-row" key={i}>
                  <div><span>Date</span><strong>{formatDate(change.dateofchange || change.DateOfTransaction)}</strong></div>
                  <div><span>From</span><strong>{change.previousvrm || change.PreviousVRM || "Unknown"}</strong></div>
                  <div><span>To</span><strong>{change.currentvrm || change.CurrentVRM || reg}</strong></div>
                </div>
              ))}
            </div>
          )}

          {rc.scrapped && (
            <div className="risk-details">
              <h3>Scrap, Export &amp; Import</h3>
              <div className="risk-grid" style={{ marginBottom: "12px" }}>
                <div className={`risk-card risk-${rc.scrapped.status ? "danger" : "clear"}`}>
                  <div className="risk-card-head">
                    <RiskOkIcon />
                    <StatusBadge status={rc.scrapped.status ? "danger" : "clear"} label={rc.scrapped.status ? "Scrapped" : "Not scrapped"} />
                  </div>
                  <p>{rc.scrapped.status ? `Scrapped on ${formatDate(rc.scrapped.date)}. A certificate of destruction ${rc.scrapped.certificateOfDestruction ? "was" : "was not"} issued.` : "Vehicle is not recorded as scrapped."}</p>
                </div>
                <div className={`risk-card risk-${rc.scrapped.exported ? "warning" : "clear"}`}>
                  <div className="risk-card-head">
                    <RiskOkIcon />
                    <StatusBadge status={rc.scrapped.exported ? "warning" : "clear"} label={rc.scrapped.exported ? "Exported" : "Not exported"} />
                  </div>
                  <p>{rc.scrapped.exported ? `Exported on ${formatDate(rc.scrapped.dateExported)}.` : "Vehicle is not recorded as exported."}</p>
                </div>
                <div className={`risk-card risk-${rc.scrapped.imported ? "warning" : "clear"}`}>
                  <div className="risk-card-head">
                    <RiskOkIcon />
                    <StatusBadge status={rc.scrapped.imported ? "warning" : "clear"} label={rc.scrapped.imported ? "Imported" : "UK vehicle"} />
                  </div>
                  <p>{rc.scrapped.imported ? `Imported vehicle${rc.scrapped.importNonEu ? " (non-EU)" : ""}. First registered in the UK on ${formatDate(rc.scrapped.dateFirstRegisteredUk)}.` : "Vehicle was first registered in the UK."}</p>
                </div>
              </div>
              {rc.scrapped.previousVrm && (
                <div className="risk-detail-row">
                  <div><span>Previous registration</span><strong>{rc.scrapped.previousVrm}</strong></div>
                  <div><span>VIN</span><strong>{rc.scrapped.vin || "Not recorded"}</strong></div>
                  <div><span>VIN last 5</span><strong>{rc.scrapped.vinLast5 || "Not recorded"}</strong></div>
                </div>
              )}
            </div>
          )}

          {rc.vic && rc.vic.count > 0 && (
            <div className="risk-details">
              <h3>VIC Inspection Record</h3>
              <div className="risk-detail-row">
                <div><span>VIC inspections</span><strong>{rc.vic.count}</strong></div>
              </div>
              {rc.vic.list && rc.vic.list.length > 0 && (
                <div className="timeline" style={{ marginTop: "12px" }}>
                  {rc.vic.list.map((vic, i) => (
                    <div className="event-card" key={i}>
                      <div className="event-marker">
                        <span className="event-dot">{i + 1}</span>
                        {i < rc.vic.list.length - 1 && <span className="event-line" />}
                      </div>
                      <div className="event-body">
                        <div className="event-header">
                          <strong>{formatDate(vic.DateOfInspection || vic.date)}</strong>
                        </div>
                        <div className="event-provider">VIC inspection carried out</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </section>
  ) : null;

  if (brand.key === "servicelad") {
    const titleMake = modelData.Make || vehicle?.make || "";
    const titleModel = modelData.Model || vi.DvlaModel || vehicle?.model || "";
    const title = (titleModel.toLowerCase().startsWith(titleMake.toLowerCase()) ? titleModel : `${titleMake} ${titleModel}`).trim() || "Vehicle report";
    const engineRows = engRows.filter(([label]) => label !== "VIN" && label !== "Engine number");
    const slDetails = {
      title,
      year: specsYear,
      engineCc: specsEngineCc ? Number(specsEngineCc) : null,
      transmission: transmissionDisplay,
      vin: fullVin,
      makeLogo,
      imageSrc: report.vehicle_image ? `/api/vehicle-image?url=${encodeURIComponent(report.vehicle_image)}` : null,
      taxAnnual: specsTaxAnnual,
      taxSixMonth: specsTaxSixMonth,
      identityRows: [
        ["Registration", displayReg],
        ["VIN / chassis", fullVin],
        ["Make and model", [vehicle?.make, vi.DvlaModel || vehicle?.model].filter(Boolean).join(" ")],
        ["First registration", vehicle?.first_used_date ? formatDate(vehicle.first_used_date) : null],
        ["Colour", vehicle?.colour],
        ["Body type", specsBodyStyle],
        ["Fuel", vehicle?.fuel_type],
        ["Engine capacity", engineSizeDisplay],
        ["Transmission", transmissionDisplay],
        ["Engine number", specsEngineNumber],
        ["CO2", specsCo2 ? `${specsCo2} g/km` : null],
        ["Euro status", specsEuroStatus],
        ["Insurance group", report.insurance_group ? `Group ${report.insurance_group}` : null],
      ],
      specGroups: [
        { title: "Performance", rows: [["Power", specsPower], ["Torque", specsTorque], ["0-60 mph", specsZeroToSixty], ["Top speed", specsTopSpeed], ["Combined MPG", specsMpg], ["Urban MPG", specsUrbanMpg], ["Extra urban MPG", specsExtraUrbanMpg]].filter(hasValue) },
        { title: "Engine", rows: engineRows },
        { title: "Size and weight", rows: dimRows },
        { title: "Body and drive", rows: [["Seats", specsSeats], ["Doors", specsDoors], ["Gears", specsGears], ["Drive type", specsDriveType]].filter(hasValue) },
      ],
      valuationRows,
      valuationMileage,
      formatValuation,
    };
    return (
      <SlCloudShell brand={brand}>
        <style>{reportCss}</style>
        <style>{slReportCss}</style>
        <header className="report-header">
          <div className="wrap report-nav">
            <Brand brand={brand} />
            <button className="pdf-btn" onClick={handleDownloadPdf} disabled={downloading}>
              {downloading ? "Generating PDF..." : "Download PDF"}
            </button>
          </div>
        </header>
        <div className="wrap" ref={reportRef}>
          <SlReport report={report} order={order} vehicle={vehicle} details={slDetails} />
        </div>
        <SlCloudFooter brand={brand} />
      </SlCloudShell>
    );
  }

  return (
    <SlCloudShell brand={brand}>
      <style>{reportCss}</style>
      <header className="report-header">
        <div className="wrap report-nav">
          <Brand brand={brand} />
          <button className="pdf-btn" onClick={handleDownloadPdf} disabled={downloading}>
            {downloading ? "Generating PDF..." : "Download PDF"}
          </button>
        </div>
      </header>

      <div className="wrap" ref={reportRef}>
        <div className="report-container">
          <div className="report-title-bar">
            <div>
              <span className="report-eyebrow">Vehicle History Report</span>
              <h1>
                {makeLogo && <img className="report-make-logo" src={makeLogo} alt="" width="34" height="34" />}
                {vehicle ? `${vehicle.make || ""} ${vehicle.model || ""}`.trim() : "Vehicle Report"}
              </h1>
              <p className="report-subtitle">
                {[vehicle?.model_year, vehicle?.fuel_type, vehicle?.colour].filter(Boolean).join(" · ")}
              </p>
            </div>
            {reg && <Plate registration={reg} />}
          </div>

          {report.vehicle_image && (
            <div className="report-vehicle-image">
              {/* Proxied through our own domain so the PDF snapshot can read
                  the pixels (the provider's storage sends no CORS headers). */}
              <img src={`/api/vehicle-image?url=${encodeURIComponent(report.vehicle_image)}`} alt={`${vehicle?.make || ""} ${vehicle?.model || ""}`} />
            </div>
          )}

          <div className="report-meta">
            <div><span>Report generated</span><strong>{formatDate(report.generated_at)}</strong></div>
            <div><span>Reference</span><strong>{order?.id?.slice(-12).toUpperCase() || "N/A"}</strong></div>
          </div>

          {isSbr && quickChecks.length > 0 && (
            <nav className="quick-checks" aria-label="Key checks">
              {quickChecks.map(({ label, value, tone, href }) => (
                <a className={`quick-check quick-${tone}`} href={href} key={label}>
                  <span>{label}</span>
                  <strong>{value}</strong>
                </a>
              ))}
            </nav>
          )}

          {isSbr && securitySection}

          {overviewMetrics.length > 0 && (
          <section className="dashboard-section overview-section">
            <div className="dashboard-heading"><div><span className="dashboard-kicker">Vehicle intelligence</span><h2>Overview</h2></div><span className="dashboard-reg">{displayReg}</span></div>
            <div className="metric-grid">
              {overviewMetrics.map(({ icon, fallbackIcon, value, label }) => (
                <div className="metric-card" key={label}>
                  <span className="metric-icon" aria-hidden="true">
                    {icon ? <img src={icon} alt="" /> : fallbackIcon}
                  </span>
                  <strong>{value}</strong>
                  <small>{label}</small>
                </div>
              ))}
            </div>
          </section>
          )}

          <div className="dashboard-columns">
            {specRows.length > 0 && <section className="dashboard-section detail-panel"><h2>Specs</h2>{specRows.map(detailRow)}</section>}
            {perfRows.length > 0 && <section className="dashboard-section detail-panel"><h2>Performance</h2>{perfRows.map(detailRow)}</section>}
          </div>

          {/* Dimensions & Engine */}
          {(dimRows.length > 0 || engRows.length > 0) && (
          <div className="dashboard-columns">
            {dimRows.length > 0 && <section className="dashboard-section detail-panel"><h2>Dimensions</h2>{dimRows.map(detailRow)}</section>}
            {engRows.length > 0 && <section className="dashboard-section detail-panel"><h2>Engine Details</h2>{engRows.map(detailRow)}</section>}
          </div>
          )}

          <section className="dashboard-section mot-summary" id="mot">
            <div><span className="dashboard-kicker">Data provided by DVSA and DVLA</span><h2>Tax &amp; MOT History</h2></div>
            <div className="mot-summary-grid">
              <div className="status-panel">
                <h3>MOT</h3>
                <div className={`status-row ${vehicle?.mot_status ? (vehicle.mot_status === "Valid" ? "good" : "bad") : ""}`}><span>MOT status</span><strong>{vehicle?.mot_status || "Not available"}</strong></div>
                <div className="status-row"><span>MOT expiry</span><strong>{formatDate(vehicle?.mot_expiry_date)}</strong></div>
                <div className="status-row"><span>Passed / failed</span><strong>{countedMots ? `${passedMots} / ${failedMots}` : "Not available"}</strong></div>
                <div className="status-row"><span>MOT pass rate</span><strong>{motPassRate !== null ? `${motPassRate}%` : "Not available"}</strong></div>
              </div>
              <div className="status-panel">
                <h3>Tax</h3>
                <div className={`status-row ${vehicle?.tax_status ? (vehicle.tax_status === "Taxed" ? "good" : "bad") : ""}`}><span>Tax status</span><strong>{vehicle?.tax_status || "Not available"}</strong></div>
                <div className="status-row"><span>Tax due</span><strong>{formatDate(vehicle?.tax_due_date)}</strong></div>
                <div className="status-row"><span>V5C issued</span><strong>{formatDate(vehicle?.v5c_issue_date)}</strong></div>
                <div className="status-row"><span>Vehicle age</span><strong>{vehicleAge != null ? `${vehicleAge} yrs` : "Not available"}</strong></div>
              </div>
              {countedMots > 0 && (
                <div className="mot-donut-panel">
                  <h3>MOT insights</h3>
                  {/* SVG ring, not conic-gradient: html2canvas drops conic-gradient from the PDF. */}
                  <div className="mot-donut">
                    <svg viewBox="0 0 178 178" width="178" height="178" aria-hidden="true">
                      <circle cx="89" cy="89" r="80.5" fill="none" stroke="#ff514c" strokeWidth="17" />
                      <circle cx="89" cy="89" r="80.5" fill="none" stroke="#159568" strokeWidth="17" pathLength="100" strokeDasharray={`${motPassRate || 0} 100`} transform="rotate(-90 89 89)" />
                    </svg>
                    <strong>{countedMots}</strong><span>Pass / fail tests</span>
                  </div>
                  <small><i className="dot pass" />Passed <i className="dot fail" />Failed</small>
                </div>
              )}
            </div>
          </section>

          {!isSbr && securitySection}

          {/* Service History */}
          {report.service_history && (
            <section className="report-section">
              <h2>Manufacturer Service History</h2>
              {report.service_history.success ? (
                report.service_history.events.length > 0 ? (
                  <div className="timeline">
                    {report.service_history.events.map((event, i) => (
                      <ServiceEventCard key={i} event={event} index={i} />
                    ))}
                  </div>
                ) : (
                  <div className="report-empty">
                    <p>No service events found for this vehicle.</p>
                    <small>Digital records depend on the manufacturer and may not be complete. A no-result response does not prove the vehicle has never been serviced.</small>
                  </div>
                )
              ) : (
                <div className="report-empty">
                  <p>Service history lookup failed: {report.service_history.error}</p>
                </div>
              )}
            </section>
          )}

          {/* Valuation: hide empty price cards. If none exist, say so. */}
          {report.valuation && (
            <section className="report-section valuation-section">
              <div className="valuation-heading">
                <div>
                  <span className="valuation-kicker">Market guide</span>
                  <h2>Vehicle valuation</h2>
                </div>
                <p>Estimated price points, not a guaranteed selling price.</p>
              </div>
              {valuationRows.length ? (
                <>
                  {report.valuation.VehicleDescription && <p className="valuation-desc">{report.valuation.VehicleDescription}</p>}
                  <div className="valuation-grid">
                    {valuationRows.map(([label, value]) => (
                      <div className={`valuation-card${label === "Private sale" ? " valuation-card-primary" : ""}`} key={label}>
                        <span>{label}</span>
                        <strong>{formatValuation(value)}</strong>
                      </div>
                    ))}
                  </div>
                  <div className="valuation-note">
                    {valuationMileage && <span>Based on {new Intl.NumberFormat("en-GB").format(valuationMileage)} miles</span>}
                    <span>Condition, location and demand can change the final price.</span>
                  </div>
                </>
              ) : (
                <div className="report-empty">
                  <p>{report.valuation.reason || "Not available for this vehicle."}</p>
                </div>
              )}
            </section>
          )}

          {/* MOT History */}
          {report.mot_history && report.mot_history.length > 0 && (
            <section className="report-section">
              <h2>MOT History</h2>
              <div className="timeline">
                {report.mot_history.map((test, i) => (
                  <div className="event-card" key={i}>
                    <div className="event-marker">
                      <span className="event-dot">{i + 1}</span>
                      {i > 0 && <span className="event-line" />}
                    </div>
                    <div className="event-body">
                      <div className="event-header">
                        <strong>{formatDate(test.completed)}</strong>
                        <StatusBadge
                          status={test.result === "PASSED" ? "clear" : "danger"}
                          label={test.result || "Unknown"}
                        />
                      </div>
                      <div className="event-provider mot-test-facts">
                        {test.mileage != null && <span>{formatMileage(test.mileage, "mi")}</span>}
                        {test.expiry && <span>Expires {formatDate(test.expiry)}</span>}
                        {test.test_class && <span>Class {test.test_class}</span>}
                        {test.test_number && <span>Test {test.test_number}</span>}
                      </div>
                      {test.defects && test.defects.length > 0 && (
                        <ul className="event-actions">
                          {test.defects.map((defect, j) => (
                            <li key={j}>
                              {defect.type && defect.type !== "UNCLASSIFIED" && <span className={`mot-defect-type mot-defect-${defect.type.toLowerCase()}`}>{defect.type}</span>}
                              {defect.text || defect}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Checks Ordered */}
          <section className="report-section">
            <h2>Checks Included</h2>
            <div className="checks-list">
              {report.checks_ordered.map((item, i) => (
                <div className="check-item" key={i}>
                  <span className="check-icon">✓</span>
                  <span>{item.label}</span>
                  <strong>£{(item.price_pence / 100).toFixed(2)}</strong>
                </div>
              ))}
            </div>
          </section>

          <div className="report-footer">
            <p>This report is provided by {brand.name}. Vehicle data comes from trusted third parties. {brand.name} is independent and unaffiliated with DVLA or DVSA.</p>
            <p>Digital records may be incomplete. A no-result response does not prove a vehicle has never been serviced or maintained.</p>
          </div>
        </div>
      </div>
      {brand.key === "servicebyreg" && <SbrFooter brand={brand} />}
      {brand.key === "servicelad" && <SlCloudFooter brand={brand} />}
    </SlCloudShell>
  );
}
